import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ServerDiscovery from '../services/ServerDiscovery';
import { authHeaders, getUser, setSession, getToken, signOut, subscribeAuth } from '../utils/auth';
import { TERMS_VERSION, TERMS_EFFECTIVE, hasAcceptedCurrentTerms } from '../legal/terms';
import { trackEvent } from '../utils/analytics';

/**
 * Asks a signed-in user to accept the current Terms of Service when their
 * account has signed an older version (or none — accounts created before
 * terms acceptance existed). Mounted once at the app root.
 */
function TermsGate() {
  const [user, setUser] = useState(getUser);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();

  useEffect(() => subscribeAuth(() => setUser(getUser())), []);

  if (!user || hasAcceptedCurrentTerms(user)) return null;
  const isUpdate = Boolean(user.termsVersion);
  // On the documents themselves the modal would cover the very text the
  // user is being asked to read. There, render a bottom bar instead and
  // leave the page scrollable.
  const readingDocs = location.pathname === '/terms' || location.pathname === '/privacy';

  const accept = async () => {
    if (!agree || busy) return;
    setBusy(true);
    setError('');
    try {
      const base = await ServerDiscovery.discoverHttpBase();
      const res = await fetch(`${base}/auth/accept-terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ termsVersion: TERMS_VERSION }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Could not record your acceptance');
      }
      const data = await res.json();
      setSession(getToken(), data.user);
      trackEvent(isUpdate ? 'terms_reaccepted' : 'terms_accepted', { version: TERMS_VERSION });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const agreement = (
    <label className="lng-terms-row">
      <input
        type="checkbox"
        checked={agree}
        onChange={(e) => setAgree(e.target.checked)}
        disabled={busy}
      />
      <span>
        I have read and agree to the{' '}
        <Link to="/terms">Terms of Service</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>
        {' '}(version {TERMS_VERSION}).
      </span>
    </label>
  );

  if (readingDocs) {
    return (
      <div className="terms-bar" role="region" aria-label="Accept the Terms of Service">
        <div className="terms-bar-inner">
          <div className="terms-bar-copy">
            <strong>{isUpdate ? 'Our Terms changed.' : 'One more step.'}</strong>{' '}
            Read the {location.pathname === '/terms' ? 'Terms' : 'Privacy Policy'} above, then accept to keep using your account.
            {error && <span className="lng-inline-error"> {error}</span>}
          </div>
          {agreement}
          <div className="terms-bar-actions">
            <button type="button" className="lng-google-btn lng-email-btn" onClick={accept} disabled={!agree || busy}>
              {busy ? 'Saving…' : 'Accept and continue'}
            </button>
            <button type="button" className="lng-text-btn" onClick={signOut} disabled={busy}>Sign out instead</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay terms-gate" role="dialog" aria-modal="true" aria-labelledby="terms-gate-title">
      <div className="modal-card gate-card">
        <h2 className="modal-title" id="terms-gate-title">
          {isUpdate ? 'We updated our Terms of Service' : 'Please accept our Terms of Service'}
        </h2>
        <p className="modal-subtitle">
          {isUpdate
            ? `The Terms changed on ${TERMS_EFFECTIVE}. Please review and accept the new version to keep using your account.`
            : 'To keep using your LangShazam account, please review and accept the Terms of Service and Privacy Policy.'}
        </p>
        {agreement}
        {error && <p className="lng-inline-error">{error}</p>}
        <button type="button" className="lng-google-btn lng-email-btn" onClick={accept} disabled={!agree || busy}>
          {busy ? 'Saving…' : 'Accept and continue'}
        </button>
        <button type="button" className="modal-btn" onClick={signOut} disabled={busy}>
          Sign out instead
        </button>
      </div>
    </div>
  );
}

export default TermsGate;
